import moment from 'moment-timezone'
import {
  CalendarIcon,
  Checkbox,
  cn,
  EnvelopeIcon,
  Paragraph,
  PassportIcon,
  PhoneIcon,
  StethoscopeIcon,
  TriangleExclamationIcon,
} from '@quickmd-llc/design-system'
import { type Patient } from '@/types/patient'
import { formatPhoneNumber } from '@quickmd-llc/frontend-utils'
import { ComplianceBadge } from './ComplianceBadge'
import React from 'react'

type Props = {
  isSelected: boolean
  onClick: (patient: Patient) => void
  patient: Patient
}

export function AccountCard({ isSelected, patient, onClick }: Props) {
  const {
    BirthDate,
    Email,
    FirstName,
    HasActiveSubscriptions,
    IsMergedMainAccount,
    LastName,
    LastSeenDateTime,
    MedicalRecordNumber,
    NextVisitDateTimeUtc,
    PhoneNumber,
    RestrictionStatus,
  } = patient

  const patientName = `${FirstName} ${LastName}`
  const patientDob = moment(BirthDate).format('MM/DD/YYYY')
  const patientPhone = formatPhoneNumber(PhoneNumber.Number)
  const recentVisitDate = LastSeenDateTime && moment(LastSeenDateTime).format('MM/DD/YYYY')
  const nextVisitDate = NextVisitDateTimeUtc && moment(NextVisitDateTimeUtc).format('MM/DD/YYYY')
  const disabled =
    HasActiveSubscriptions || IsMergedMainAccount || Boolean(nextVisitDate) || RestrictionStatus === 'merged'

  function handleClickCard() {
    if (disabled) {
      return
    }

    onClick(patient)
  }

  function handleClickCheckbox(event: React.MouseEvent) {
    event.stopPropagation()
    onClick(patient)
  }

  return (
    <div
      data-testid={`pw_account-${patient.Email}`}
      className={cn(
        'grid grid-cols-[min-content_auto] items-start gap-sp-1 rounded-lg border border-border-dark p-sp-2 shadow-sm',
        {
          'border-border-highlight': isSelected,
          'hover:border-border-highlight': isSelected,
          'hover:border-coastal-blue-20 hover:bg-surface-highlight': !disabled,
          'cursor-default bg-surface-disabled': disabled,
        },
      )}
      onClick={handleClickCard}
      tabIndex={0}
      role='button'
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClickCard()
        }
      }}>
      <div>
        <Checkbox checked={isSelected} disabled={disabled} onClick={(event) => handleClickCheckbox(event)} />
      </div>

      <div>
        <div className='mb-sp-0.5 flex items-center gap-sp-0.5'>
          <Paragraph className='font-semibold text-text-dark'>{patientName}</Paragraph>
          <ComplianceBadge patient={patient} />
        </div>

        <div className='grid gap-sp-1'>
          <div className='flex flex-wrap gap-sp-1'>
            <div className='flex items-center gap-sp-0.5'>
              <EnvelopeIcon className='text-text-medium' />

              <Paragraph>{Email}</Paragraph>
            </div>

            <div className='flex items-center gap-sp-0.5'>
              <CalendarIcon className='text-text-medium' />

              <Paragraph>- DOB {patientDob}</Paragraph>
            </div>

            <div className='flex items-center gap-sp-0.5'>
              <PhoneIcon className='text-text-medium' />

              <Paragraph>{patientPhone}</Paragraph>
            </div>

            <div className='flex items-center gap-sp-0.5'>
              <PassportIcon className='text-text-medium' />

              <Paragraph>MRN - {MedicalRecordNumber}</Paragraph>
            </div>

            {recentVisitDate ?
              <div className='flex items-center gap-sp-0.5'>
                <StethoscopeIcon className='text-text-medium' />

                <Paragraph>Recent visit - {recentVisitDate}</Paragraph>
              </div>
            : null}
          </div>

          <div className='grid gap-sp-1'>
            {nextVisitDate ?
              <div className='flex items-center gap-sp-0.5'>
                <TriangleExclamationIcon iconSize='l' className='text-text-error' />{' '}
                <Paragraph className='text-text-error'>Upcoming visit - {nextVisitDate}</Paragraph>
              </div>
            : null}

            {HasActiveSubscriptions ?
              <div className='flex items-center gap-sp-0.5'>
                <TriangleExclamationIcon iconSize='l' className='text-text-error' />{' '}
                <Paragraph className='text-text-error'>Active subscription</Paragraph>
              </div>
            : null}

            {IsMergedMainAccount ?
              <div className='flex items-center gap-sp-0.5'>
                <TriangleExclamationIcon iconSize='l' className='text-text-highlight' />{' '}
                <Paragraph className='text-text-highlight'>Main account</Paragraph>
              </div>
            : null}
          </div>
        </div>
      </div>
    </div>
  )
}
